import { ProfileTabIcon } from '@/components/navigation/profile-tab-icon';
import { TabBarIcon } from '@/components/navigation/tab-bar-icon';
import { useAuth } from '@/hooks/use-auth';
import { useCurrentUserProfile } from '@/hooks/use-profile';
import { useResponsiveShell } from '@/providers/responsive-shell-provider';
import { useAppTheme } from '@/providers/theme-provider';
import { imageUrl } from '@/utils/image-url';
import { router, Tabs } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

export default function TabLayout() {
  const theme = useAppTheme();
  const { isDesktop, onCheckIn, unreadCount } = useResponsiveShell();
  const { user: authUser } = useAuth();
  const { data: profileData } = useCurrentUserProfile();

  // Use profile data if available (updates immediately after upload), otherwise fall back to auth user
  const user = profileData?.user || authUser;
  const avatarUrl = imageUrl(user?.image, 'thumbnail') || undefined;

  // Desktop layout - sidebar handles navigation, hide tab bar
  if (isDesktop) {
    return (
      <Tabs
        screenOptions={{
          tabBarStyle: { display: 'none' },
          headerShown: false,
        }}
      >
        <Tabs.Screen name="index" options={{ href: null }} />
        <Tabs.Screen name="activity" options={{ title: 'Activity' }} />
        <Tabs.Screen name="discover" options={{ title: 'Discover' }} />
        <Tabs.Screen name="add" options={{ href: null }} />
        <Tabs.Screen name="notifications" options={{ title: 'Notifications' }} />
        <Tabs.Screen name="profile" options={{ title: 'Profile' }} />
      </Tabs>
    );
  }

  // Mobile layout with bottom tabs
  return (
    <Tabs
      screenOptions={{
        tabBarActiveTintColor: theme.colors.tabIconSelected,
        tabBarInactiveTintColor: theme.colors.tabIconDefault,
        tabBarShowLabel: true,
        headerShown: false,
        tabBarStyle: {
          height: 60,
          backgroundColor: theme.colors.tabBarBackground,
          borderTopColor: theme.colors.border,
        },
      }}
    >
      <Tabs.Screen name="index"
        options={{
          href: null,
        }}
      />
      <Tabs.Screen name="activity"
        options={{
          title: 'Activity',
          tabBarIcon: ({ color, focused }) => (
            <TabBarIcon name={focused ? 'pulse' : 'pulse-outline'} color={color} />
          ),
        }}
      />

      <Tabs.Screen name="discover"
        options={{
          title: 'Discover',
          tabBarIcon: ({ color, focused }) => (
            <TabBarIcon name={focused ? 'search' : 'search-outline'} color={color} />
          ),
        }}
      />

      <Tabs.Screen name="add"
        options={{
          title: '',
          tabBarIcon: () => (
            <View
              className="justify-center items-center rounded-full bg-primary"
              style={{
                height: 80,
                width: 80,
                borderColor: theme.colors.tabBarBackground,
                borderWidth: 7,
              }}
            >
              <TabBarIcon name="restaurant" size={30} />
            </View>
          ),
        }}
        listeners={{
          tabPress: (e) => {
            e.preventDefault();
            onCheckIn();
          },
        }}
      />

      <Tabs.Screen name="notifications"
        options={{
          title: 'Notifications',
          tabBarIcon: ({ color, focused }) => (
            <TabBarIcon
              name={focused ? 'notifications' : 'notifications-outline'}
              color={color}
            />
          ),
          tabBarBadge: unreadCount && unreadCount > 0 ? unreadCount : undefined,
          tabBarBadgeStyle: {
            backgroundColor: theme.colors.primary,
            color: theme.colors.flavrly[900],
            fontWeight: 'bold',
          },
        }}
      />

      <Tabs.Screen name="profile"
        options={{
          title: 'Profile',
          tabBarIcon: ({ color, focused }) => (
            <ProfileTabIcon color={color} focused={focused} avatarUrl={avatarUrl} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            // Always navigate to the current user's profile index when Profile tab is pressed
            // This prevents restoring nested routes like friend profiles
            e.preventDefault();
            router.replace('/(tabs)/profile');
          },
        }}
      />
    </Tabs>
  );
}
